import { Menu as BaseMenu } from '@base-ui/react/menu';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';
import { anchoredPopupMotion } from '../lib/motion';

/**
 * 동작 목록(행 더보기 등). 값을 고르면 `Select`, 화면을 옮기면 링크다.
 * 파괴적 항목은 맨 아래 `Menu.Separator` 뒤에 `tone="danger"`로 두고 `ConfirmDialog`로 잇는다.
 */
export const MenuRoot = BaseMenu.Root;
export const MenuTrigger = BaseMenu.Trigger;
export const MenuGroup = BaseMenu.Group;

export interface MenuContentProps extends ComponentProps<typeof BaseMenu.Popup> {
  sideOffset?: number;
  align?: ComponentProps<typeof BaseMenu.Positioner>['align'];
}

export function MenuContent({
  sideOffset = 6,
  align = 'start',
  className,
  ...props
}: MenuContentProps) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner sideOffset={sideOffset} align={align} className="z-popover">
        <BaseMenu.Popup
          className={cn(
            'border-line bg-surface-raised shadow-pop min-w-48 rounded-xl border p-1 outline-none',
            anchoredPopupMotion,
            className,
          )}
          {...props}
        />
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

export interface MenuItemProps extends ComponentProps<typeof BaseMenu.Item> {
  /** @default 'default' — 'danger'는 맨 아래 구분선 뒤에만 */
  tone?: 'default' | 'danger';
}

/** 하이라이트 이동(↑↓)은 움직이지 않는다 — 배경이 즉시 바뀐다(D3 §4.5). */
export function MenuItem({ tone = 'default', className, ...props }: MenuItemProps) {
  return (
    <BaseMenu.Item
      className={cn(
        'gap-snug px-row py-snug flex cursor-default items-center rounded-lg text-sm outline-none select-none',
        'data-[highlighted]:bg-highlight data-[disabled]:opacity-50',
        tone === 'danger' ? 'text-danger' : 'text-ink',
        className,
      )}
      {...props}
    />
  );
}

/** 화면을 옮기는 항목 — `render={<Link … />}`로 링크를 넘긴다. 모양은 `MenuItem`과 같다. */
export function MenuLinkItem({ className, ...props }: ComponentProps<typeof BaseMenu.LinkItem>) {
  return (
    <BaseMenu.LinkItem
      className={cn(
        'gap-snug px-row py-snug text-ink flex cursor-default items-center rounded-lg text-sm outline-none select-none',
        'data-[highlighted]:bg-highlight',
        className,
      )}
      {...props}
    />
  );
}

export function MenuSeparator({ className, ...props }: ComponentProps<typeof BaseMenu.Separator>) {
  return <BaseMenu.Separator className={cn('bg-line mx-1 my-1 h-px', className)} {...props} />;
}

export const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Content: MenuContent,
  Item: MenuItem,
  LinkItem: MenuLinkItem,
  Group: MenuGroup,
  Separator: MenuSeparator,
};
